import React from "react";
import {
  Box,
  Paper,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Grid,
  Typography,
  Divider,
} from "@mui/material";
import { Save, Cancel } from "@mui/icons-material";
import { UseFormReturn } from "react-hook-form";
import { CreatePromptTemplateDto } from "@/dto/PromptTemplateDto";

interface CreateFormProps {
  form: UseFormReturn<CreatePromptTemplateDto>;
  loading: boolean;
  onSubmit: (data: CreatePromptTemplateDto) => void;
  onCancel: () => void;
}

const AI_PROVIDERS = [
  { value: "OPENAI", label: "OpenAI" },
  { value: "ANTHROPIC", label: "Anthropic" },
  { value: "GOOGLE", label: "Google" },
];

const AI_MODELS = {
  OPENAI: ["gpt-4o", "gpt-4o-mini", "gpt-4-turbo", "gpt-3.5-turbo"],
  ANTHROPIC: ["claude-3-5-sonnet-20241022", "claude-3-opus-20240229", "claude-3-haiku-20240307"],
  GOOGLE: ["gemini-pro", "gemini-pro-vision"],
};

const CATEGORIES = ["boss_baby", "ai_manager", "customer_service", "content_generation"];

export default function CreateForm({ form, loading, onSubmit, onCancel }: CreateFormProps) {
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = form;

  const selectedProvider = watch("aiModelProvider");

  return (
    <Paper sx={{ p: 3 }}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              Basic Information
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              {...register("templateKey", {
                required: "Template key is required",
                maxLength: { value: 100, message: "Template key must be less than 100 characters" },
              })}
              label="Template Key *"
              fullWidth
              error={!!errors.templateKey}
              helperText={errors.templateKey?.message}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              {...register("templateName", {
                required: "Template name is required",
                maxLength: { value: 200, message: "Template name must be less than 200 characters" },
              })}
              label="Template Name *"
              fullWidth
              error={!!errors.templateName}
              helperText={errors.templateName?.message}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth error={!!errors.templateCategory}>
              <InputLabel>Category *</InputLabel>
              <Select
                {...register("templateCategory", { required: "Category is required" })}
                label="Category *"
                defaultValue=""
              >
                {CATEGORIES.map((category) => (
                  <MenuItem key={category} value={category}>
                    {category}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              {...register("priorityOrder")}
              label="Priority Order"
              type="number"
              fullWidth
              inputProps={{ min: 1 }}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              {...register("templateDescription")}
              label="Description"
              multiline
              rows={3}
              fullWidth
            />
          </Grid>

          <Grid item xs={12}>
            <Divider />
            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
              AI Model Configuration
            </Typography>
          </Grid>

          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>AI Provider</InputLabel>
              <Select
                {...register("aiModelProvider")}
                label="AI Provider"
                defaultValue="OPENAI"
              >
                {AI_PROVIDERS.map((provider) => (
                  <MenuItem key={provider.value} value={provider.value}>
                    {provider.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>AI Model</InputLabel>
              <Select
                {...register("aiModelName")}
                label="AI Model"
                defaultValue="gpt-4o"
              >
                {AI_MODELS[selectedProvider as keyof typeof AI_MODELS]?.map((model) => (
                  <MenuItem key={model} value={model}>
                    {model}
                  </MenuItem>
                )) || []}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={2}>
            <TextField
              {...register("aiModelTemperature", {
                min: { value: 0, message: "Temperature must be between 0 and 2" },
                max: { value: 2, message: "Temperature must be between 0 and 2" },
              })}
              label="Temperature"
              type="number"
              fullWidth
              inputProps={{ min: 0, max: 2, step: 0.1 }}
              error={!!errors.aiModelTemperature}
              helperText={errors.aiModelTemperature?.message}
            />
          </Grid>

          <Grid item xs={12} md={2}>
            <TextField
              {...register("aiMaxTokens")}
              label="Max Tokens"
              type="number"
              fullWidth
              inputProps={{ min: 1 }}
            />
          </Grid>

          <Grid item xs={12}>
            <Divider />
            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
              Prompt Configuration
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <TextField
              {...register("systemPrompt")}
              label="System Prompt"
              multiline
              rows={4}
              fullWidth
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              {...register("userPromptTemplate")}
              label="User Prompt Template"
              multiline
              rows={4}
              fullWidth
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              {...register("assistantPrompt")}
              label="Assistant Prompt"
              multiline
              rows={3}
              fullWidth
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              {...register("promptVariables")}
              label="Prompt Variables (JSON)"
              multiline
              rows={4}
              fullWidth
              placeholder='{"variable1": "default_value"}'
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              {...register("aiModelConfig")}
              label="AI Model Config (JSON)"
              multiline
              rows={4}
              fullWidth
              placeholder='{"max_tokens": 4000}'
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              {...register("useCondition")}
              label="Use Condition"
              multiline
              rows={2}
              fullWidth
            />
          </Grid>

          <Grid item xs={12}>
            <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
              <Button variant="outlined" startIcon={<Cancel />} onClick={onCancel}>
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                startIcon={<Save />}
                disabled={loading}
              >
                Create Template
              </Button>
            </Box>
          </Grid>
        </Grid>
      </form>
    </Paper>
  );
}